import { Box, BoxProps, Flex, FlexProps, Heading, HeadingProps } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SanityImage } from '~/components/sanity-image';
import { SanityImageType } from '~/features/page-template/models/image';

export type Logo = {
  name: string;
  lofiLogoDark: SanityImageType | null;
  lofiLogoLight: SanityImageType | null;
  hifiLogo: SanityImageType | null;
};

type Theme = 'light' | 'dark';

export type LogoGardenSectionProps = {
  title: string;
  logos: Array<Logo>;
  theme?: Theme;
  logoContainerProps?: FlexProps;
  wrapLogos?: boolean;
  boxProps?: BoxProps;
  headingProps?: HeadingProps;
};

export function LogoGardenSection({
  logos,
  title,
  theme = 'dark',
  logoContainerProps,
  wrapLogos = true,
  boxProps,
  headingProps,
}: LogoGardenSectionProps) {
  if (!logos?.length) return null;
  return (
    <Box
      as='section'
      backgroundColor={theme === 'dark' ? 'blue.800' : 'accent.sky.100'}
      color={theme === 'dark' ? 'white' : 'black'}
      display='flex'
      flexDirection='column'
      justifyContent='center'
      minHeight='256px'
      padding={{
        base: '40px 0px',
        md: '64px 0px',
      }}
      {...boxProps}
    >
      <ContentContainer>
        <Heading as='h2' size='h2' textAlign='center' {...headingProps}>
          {title}
        </Heading>
        <Box height='40px' />
        <Flex
          justifyContent='center'
          flexWrap={{
            base: 'wrap',
            md: wrapLogos ? 'wrap' : 'nowrap',
          }}
          gap={{
            base: '33px 24px',
            md: '31px 80px',
          }}
          {...logoContainerProps}
        >
          {logos.map((logo) => (
            <Logo key={logo.name} logo={logo} theme={theme} />
          ))}
        </Flex>
      </ContentContainer>
    </Box>
  );
}

function Logo({ logo, theme }: { logo: Logo; theme: Theme }) {
  const image = theme === 'dark' ? logo.lofiLogoLight : logo.lofiLogoDark;
  if (!image) return null;
  return <SanityImage alt={logo.name} image={image} quality={90} sizes='500px' />;
}
